import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  Inject,
  OnDestroy
} from '@angular/core';
import { DedupeType } from '../models/dedupe-type';
import { UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { AppState } from '../reducers';
import { AudienceBuilderService } from '../audience-builder/audience-builder.service';
import { FeatureService } from '../utils/feature-service';
import { ActivatedRoute, Router } from '@angular/router';
import { UtilsService } from '../utils/utilservice';
import { FEATURE_AUDIENCES } from '../utils/feature-utils';
import { selectContext, selectContextId } from '../context/context.reducer';
import { CabConstants } from '../cab.constants';
import { combineLatest, filter, Subscription } from 'rxjs';
import { isDefined } from '../utils/utils';
import {
  selectAttributeDetails,
  selectDataTypeOperators
} from '../audience-builder/audience-builder.reducer';
import { CountsService } from '../counts/counts.service';
import { SetUnsavedChangesState } from '../hasUnsavedChanges/hasUnsavedChanges.actions';
import { CabCommonService } from '@epsilon-cdp/cab-common-lib';
import { CountInfo } from '../models/criteria-type';
import { selectDataUniverses } from '../data-universe/data-universe.reducer';

@UntilDestroy()
@Component({
  selector: 'lib-criteria',
  templateUrl: './criteria.component.html',
  styleUrl: './criteria.component.sass',
  standalone: false
})
export class CriteriaComponent implements OnInit, OnDestroy {
  @Input() activeDedupeType: DedupeType;
  @Input() audienceForm: UntypedFormGroup;
  @Input() isEdit: boolean;
  @Input() disableRunCount = false;
  @Output() submitUpdateCount: EventEmitter<any> = new EventEmitter<any>();

  dedupeDisplayName: string | DedupeType;
  contextId: string;
  dataUniverseId: string;
  isProductTypeDcdp: boolean;
  audiencesEnabled: boolean;
  dedupeTdentityTypes = [];
  businessUnitId: string;
  audienceDefinitionId: string;
  criteria = {
    includeConditions: [],
    excludeConditions: [],
    nodeDetails: {},
    queryFlags: {},
    cabId: ''
  };
  operators = [];
  showCriteriaErrors = false;
  countInfo: CountInfo;
  isFormValid = true;
  subscriptions: Subscription[] = [];
  informationMessage: string;

  constructor(
    public store: Store<AppState>,
    public builderService: AudienceBuilderService,
    public featureService: FeatureService,
    private route: ActivatedRoute,
    private utilsService: UtilsService,
    public countsService: CountsService,
    public router: Router,
    private cabCommonService: CabCommonService,
    @Inject('CRITERIA_SECTION_SERVICE') private criteriaService: any
  ) {}

  ngOnInit(): void {
    this.criteriaService.countDetailsUpdate.next(null);
    this.audiencesEnabled =
      this.featureService.isFeatureEnabled(FEATURE_AUDIENCES);
    this.store
      .select(selectContext)
      .pipe(untilDestroyed(this))
      .subscribe((context) => {
        if (context?.productType === CabConstants.DCDP_PRODUCT_NAME) {
          this.isProductTypeDcdp = true;
        }
      });
    combineLatest([
      this.builderService.activeDataUniverseId$,
      this.store.select(selectContextId).pipe(filter(isDefined)),
      this.store.select(selectDataUniverses).pipe(filter(isDefined))
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([dataUniverseId, contextId, dedupeTdentityTypes]) => {
        this.contextId = contextId;
        this.dataUniverseId = dataUniverseId;
        if (this.criteriaService) {
          this.criteriaService.updateAPIParams(
            this.contextId,
            this.dataUniverseId
          );
        }
        this.dedupeTdentityTypes = dedupeTdentityTypes.find(i => i.cabContextId === this.contextId && i.id != 'all')?.dedupeTypes || [];
      });
    this.businessUnitId = this.utilsService.getBusinessUnitId(this.route);
    this.audienceDefinitionId =
      this.route.snapshot.paramMap.get('definitionId');
    this.store
      .select(selectDataTypeOperators)
      .pipe(filter(isDefined), untilDestroyed(this))
      .subscribe((operators: any) => {
        this.operators = operators;
      });
    this.store
      .select(selectAttributeDetails)
      .pipe(untilDestroyed(this))
      .subscribe((details) => {
        this.criteria.nodeDetails = details;
      });

    this.dedupeDisplayName = this.getDedupeType();
    this.fetchAudienceDetails();

    this.subscriptions.push(
      this.cabCommonService.onValueUpdate$.subscribe(
        (valueUpdated: boolean) => {
          if (valueUpdated) {
            this.countsService.stopCron();
            this.countsService.resetBuilderCounts();
          }
        }
      )
    );
    this.subscriptions.push(
      this.criteriaService.countDetailsUpdate.subscribe((updatedCount) => {
        this.updateCountDetails(
          updatedCount?.totalIncludecount,
          updatedCount?.totalExcludeCount,
          updatedCount?.includeGroupCounts
        );
      })
    );
  }

  fetchAudienceDetails() {
    this.subscriptions.push(
      this.route.data.subscribe((response: any) => {
        const audienceData = response?.audienceData?.entity;
        if (audienceData?.audienceDefinition?.query) {
          this.criteria.includeConditions =
            audienceData.audienceDefinition.query.includeConditions || [];
          this.criteria.excludeConditions =
            audienceData.audienceDefinition.query.excludeConditions || [];
          this.criteria.queryFlags =
            audienceData.audienceDefinition.query.queryFlags;
          if (audienceData.audienceDefinition.count) {
            this.criteriaService.countDetailsUpdate.next({
              totalIncludecount: audienceData.audienceDefinition.count.count,
              totalExcludeCount:
                audienceData.audienceDefinition.count.totalExcludeGroupCount,
              includeGroupCounts:
                audienceData.audienceDefinition.count.includeGroupCounts
            });
          }
          this.criteria.cabId = audienceData.audienceDefinition.cabId;
        }

        const errorDetails = response?.audienceData?.entity?.errors;
        if (errorDetails && errorDetails.length) {
          this.informationMessage = errorDetails[0].errorMessage;
        }
      })
    );
  }

  getDedupeType() {
    if (this.isProductTypeDcdp) {
      return this.builderService.getAudienceDisplayName(
        this.audienceForm?.get('dedupeType')?.value ||
          this.builderService.audience?.dedupeIdentityType,
        this.audienceForm?.get('channelType')?.value ||
          this.builderService.audience?.audienceAttributes?.channelType
      );
    }
    return this.dedupeDisplayName || this.activeDedupeType;
  }

  onValueChanges(attributeFormDetails) {
    this.store.dispatch(new SetUnsavedChangesState('pending'));
    this.isFormValid = attributeFormDetails.isValid;
    this.criteriaService.countDetailsUpdate.next(null);
  }

  updateCountDetails(totalIncludecount, totalExcludeCount, includeGroupCounts) {
    this.countInfo = {
      totalIncludecount,
      totalExcludeCount,
      includeGroupCounts
    };
  }

  isSaveButtonDisabled() {
    return !this.builderService.definitionHasAttribute$.value;
  }

  triggerSubmitUpdateCount(event) {
    this.submitUpdateCount.emit(event);
  }

  triggerHandleInvalidDefinitionError() {
    // this.handleInvalidDefinitionError.emit();
  }

  cancelUpdateCount() {
    this.countsService.stopCron();
  }

  setShowCriteriaErrors(value: boolean) {
    this.showCriteriaErrors = value;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
